import React from 'react';
import clsx from 'clsx';
import styles from '../MagicSignTable.module.scss';
import type { MagicSignSortBy, MagicSignSortDir } from '../../../types';
import SwapVertIcon from '../../../../../assets/swap_vert.svg';
import SwapVertUpIcon from '../../../../../assets/swap_vert_up.svg';
import SwapVertDownIcon from '../../../../../assets/swap_vert_down.svg';

interface SortToggleProps {
    label: string;
    field: MagicSignSortBy;
    currentSortBy?: string;
    currentSortDir?: string;
    onSort: (field: MagicSignSortBy, dir: MagicSignSortDir | undefined) => void;
}

/**
 * Sort toggle for a column header — cycles: none → asc → desc → none.
 * Uses the same swap_vert SVG assets as the Deals table SortIcon component
 * so the icon is visually identical across tables.
 */
const SortToggle: React.FC<SortToggleProps> = ({ label, field, currentSortBy, currentSortDir, onSort }) => {
    const isActive = currentSortBy === field;
    const nextDir = (): MagicSignSortDir | undefined => {
        if (!isActive) return 'asc';
        if (currentSortDir === 'asc') return 'desc';
        return undefined; // clear
    };

    const sortIconSrc =
        isActive && currentSortDir === 'asc' ? SwapVertUpIcon : isActive && currentSortDir === 'desc' ? SwapVertDownIcon : SwapVertIcon;

    const altText =
        isActive && currentSortDir === 'asc' ? 'Sort ascending' : isActive && currentSortDir === 'desc' ? 'Sort descending' : 'Sort';

    return (
        <button
            className={clsx(styles.SortToggleBtn, { [styles.SortToggleBtnActive]: isActive })}
            onClick={(e) => {
                e.stopPropagation();
                onSort(field, nextDir());
            }}
            aria-label={`Sort by ${label}`}
        >
            {label}
            <span className={clsx(styles.SortIcon, { [styles.SortIconActive]: isActive })}>
                <img src={sortIconSrc} alt={altText} className={styles.SortIconImg} />
            </span>
        </button>
    );
};

export default SortToggle;
